import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { parseApiError } from "../../lib/api";
import { formatMoney, normalizeCurrency } from "../../lib/money";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* ---------- Tab ---------- */
const BookingsForPayoutTab = () => {
  usePartnerTranslation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/finance/summary`);
        if (!cancelled) setSummary(res.data);
      } catch (err) {
        const { message } = parseApiError(err, tr("Couldn't load finance summary."));
        if (process.env.NODE_ENV !== "production") {
          console.warn("[finance/summary] load failed", message, err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const cur = normalizeCurrency(summary?.currency);
  const bookings = summary?.current_month_bookings || [];
  const count = summary?.confirmed_booking_count || bookings.length;
  const bookingTotal = bookings.reduce(
    (sum, booking) => sum + Number(booking.net_amount || 0),
    0,
  );
  const periodLabel = summary?.period_start && summary?.period_end
    ? `${formatDate(summary.period_start)} - ${formatDate(summary.period_end)}`
    : tr("This month");

  return (
    <section data-testid="tab-bookings-for-payout-content" className="mt-6">
      {/* Hero */}
      <div
        data-testid="payout-hero"
        className="rounded-2xl border border-[#EAECEF] bg-gradient-to-br from-[#FFF8F4] via-white to-[#FFF2F2] p-6 sm:p-8"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#6B7280]">{tr("Estimated payout")}</p>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-3">
          {loading ? (
            <Loader2 className="h-8 w-8 animate-spin text-[#FF5757]" />
          ) : (
            <>
              <p
                data-testid="payout-amount"
                className="text-3xl font-extrabold tracking-tight text-[#0F1115] sm:text-4xl md:text-5xl"
              >
                {formatMoney(summary?.pending_balance, cur)}
              </p>
              <p className="text-sm text-[#6B7280]">{tr("from ")}{count}{tr(" confirmed ")}{count === 1 ? tr("booking") : tr("bookings")}
              </p>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-[#EAECEF] bg-white">
        <div className="flex flex-col gap-2 border-b border-[#EAECEF] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h3 className="text-base font-bold text-[#0F1115]">{tr("Valid bookings for this month")}</h3>
            <p className="text-sm text-[#6B7280]">{periodLabel}</p>
          </div>
          <div className="text-start sm:text-end">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B7280]">{tr("List total")}</p>
            <p data-testid="current-month-booking-total" className="text-lg font-extrabold text-[#0F1115]">
              {loading ? "—" : formatMoney(bookingTotal, cur)}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-7 w-7 animate-spin text-[#FF5757]" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="text-sm font-semibold text-[#0F1115]">{tr("No valid bookings for this month yet.")}</p>
            <p className="mt-1 text-sm text-[#6B7280]">{tr("Confirmed bookings that have not already been paid out will appear here.")}</p>
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-[#EAECEF] text-sm">
                <thead className="bg-[#FAFAFB] text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">
                  <tr>
                    <th className="px-5 py-3 text-start">{tr("Booking")}</th>
                    <th className="px-5 py-3 text-start">{tr("Product")}</th>
                    <th className="px-5 py-3 text-start">{tr("Activity")}</th>
                    <th className="px-5 py-3 text-end">{tr("Guests")}</th>
                    <th className="px-5 py-3 text-end">{tr("Gross")}</th>
                    <th className="px-5 py-3 text-end">{tr("Commission")}</th>
                    <th className="px-5 py-3 text-end">{tr("Net")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EAECEF]">
                  {bookings.map((booking) => {
                    const rowCurrency = normalizeCurrency(booking.currency, cur);
                    return (
                      <tr key={booking.id}>
                        <td className="px-5 py-4 font-semibold text-[#0F1115]">
                          {booking.booking_reference}
                        </td>
                        <td className="max-w-[240px] px-5 py-4 text-[#374151]">
                          <span className="line-clamp-2">{booking.product_title}</span>
                        </td>
                        <td className="px-5 py-4 text-[#374151]">
                          <div>{formatDate(booking.activity_date)}</div>
                          {booking.activity_time && (
                            <div className="text-xs text-[#6B7280]">{booking.activity_time}</div>
                          )}
                        </td>
                        <td className="px-5 py-4 text-end text-[#374151]">
                          {booking.traveller_count}
                        </td>
                        <td className="px-5 py-4 text-end text-[#374151]">
                          {formatMoney(booking.gross_amount, rowCurrency)}
                        </td>
                        <td className="px-5 py-4 text-end text-[#374151]">
                          {formatMoney(booking.commission_amount, rowCurrency)}
                        </td>
                        <td className="px-5 py-4 text-end font-bold text-[#0F1115]">
                          {formatMoney(booking.net_amount, rowCurrency)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-[#EAECEF] md:hidden">
              {bookings.map((booking) => {
                const rowCurrency = normalizeCurrency(booking.currency, cur);
                return (
                  <article key={booking.id} className="px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#0F1115]">{booking.booking_reference}</p>
                        <p className="mt-1 text-sm text-[#374151]">{booking.product_title}</p>
                      </div>
                      <p className="shrink-0 text-end font-bold text-[#0F1115]">
                        {formatMoney(booking.net_amount, rowCurrency)}
                      </p>
                    </div>
                    <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">{tr("Activity")}</dt>
                        <dd className="mt-1 text-[#374151]">
                          {formatDate(booking.activity_date)}
                          {booking.activity_time ? `, ${booking.activity_time}` : ""}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">{tr("Guests")}</dt>
                        <dd className="mt-1 text-[#374151]">{booking.traveller_count}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">{tr("Gross")}</dt>
                        <dd className="mt-1 text-[#374151]">
                          {formatMoney(booking.gross_amount, rowCurrency)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]">{tr("Commission")}</dt>
                        <dd className="mt-1 text-[#374151]">
                          {formatMoney(booking.commission_amount, rowCurrency)}
                        </dd>
                      </div>
                    </dl>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default BookingsForPayoutTab;
